/* eslint-disable max-len */

import { ProjectDesignSettingButtons } from './action-button-config';
import { CollapsePanels } from './collapse-panel-config';
import {
  apiEndpointsStepIntroductionContent,
  apiEndpointsStepIntroductionExtraContent,
  dataModelsAndDatabaseStepsIntroductionContent,
  dataModelsAndDatabaseStepsIntroductionExtraContent,
  designSystemStepIntroductionContent,
  designSystemStepIntroductionExtraContent,
  environmentVariablesStepIntroductionContent,
  environmentVariablesStepIntroductionExtraContent,
  generateProjectCodeStepIntroductionContent,
  generateProjectCodeStepIntroductionExtraContent,
  messageTemplatesStepIntroductionContent, messageTemplatesStepIntroductionExtraContent,
  projectDescriptionStepIntroductionContent,
  projectDescriptionStepIntroductionExtraContent,
  projectInformationStepIntroductionContent,
  projectInformationStepIntroductionExtraContent, projectMembersStepContent,
  projectMembersStepIntroductionContent, projectMembersStepIntroductionExtraContent,
  scopeOfWorkStepIntroductionContent,
  scopeOfWorkStepIntroductionExtraContent,
  serverSideBusinessProcessesStepIntroductionContent,
  serverSideBusinessProcessesStepIntroductionExtraContent, themeStepIntroductionContent,
  themeStepIntroductionExtraContent,
  uiBusinessProcessesStepIntroductionContent,
  uiBusinessProcessesStepIntroductionExtraContent,
  uiPageLayoutsStepIntroductionContent,
  uiPageLayoutsStepIntroductionExtraContent,
  uiPagesStepIntroductionContent,
  uiPagesStepIntroductionExtraContent,
  welcomeStepContent,
  welcomeStepIntroductionContent,
  welcomeStepIntroductionExtraContent
} from './wizard-steps-content';
import { IntroductionNextButton } from './wizard-rabbit-introduction-next-button.enum';
import { ProjectDesignTaskIdentifier } from '../state/project-design-task/project-design-task.interface';
import { LayoutMode } from './layoutMode.enum';

export enum WizardStepStatus {
  NotStarted = 'not-started',
  Introduction = 'introduction',
  InProgress = 'in-progress',
  Completed = 'completed',
  Error = 'error',
  ScaffoldedFromTemplate = 'scaffolded-from-template', // This status is used to indicate that the step is scaffolded from a template without AI improvement,
  Optional = 'optional',
  Skipped = 'skipped', // This status is used to indicate that the step is skipped
}

export enum AIProgressStatus {
  Unknown = 'unknown',
  NotStarted = 'not-started',
  InProgress = 'in-progress',
  Completed = 'completed',
  Error = 'error'
}

export interface WizardStepConfig {
  key: WizardSteps; // Assuming WizardSteps is an enum or type elsewhere in the code
  projectDesignTaskIdentifier?: ProjectDesignTaskIdentifier;
  title: string;
  planTitle?: string;
  description: string;
  introduction?: string;
  introductionExtra?: string;
  collapsePanel: CollapsePanels; // Assuming CollapsePanels is an enum or type elsewhere in the code
  visiblePanels?: CollapsePanels[]; // Array of CollapsePanels
  icon: {
    name: string;
  };
  highlightedElements: string[];
  isCompleted: boolean;
  status?: WizardStepStatus; // Assuming WizardStepStatus is an enum or type elsewhere in the code
  disableNextButton?: boolean;
  skippable?: boolean;
  layoutPaths?: Record<string, string>;
  layoutMode?: string;
  actionConfigs?: Record<string, any>;
  introductionNextButtonTitle?: string; // Title of the next button in the introduction (rabbit) content
  displayInPlanningTimeline?: boolean;
  autoSkipDurationOnPlanning?: number; // Duration in milliseconds after which the step should be auto-skipped in planning/introduction
  autoSkipDurationOnDesignView?: number; // Duration in milliseconds after which the step should be auto-skipped in design view
}


export enum WizardSteps {
  Welcome = 'welcome',
  ProjectDescription = 'projectDescription',
  ProjectInformation = 'projectInformation',
  ScopeOfWork = 'scopeOfWork',
  DataModelsAndDatabase = 'dataModelsAndDatabase',
  APIEndpoints = 'apiEndpoints',
  UIPages = 'uiPages',
  UIPageLayouts = 'uiPageLayouts',
  Theme = 'theme',
  DesignSystem = 'designSystem',
  UIBusinessProcesses = 'uiBusinessProcesses',
  MessageTemplates = 'messageTemplates',
  ServerSideBusinessProcesses = 'serverSideBusinessProcesses',
  SettingsVariablesAndEnvironments = 'settingsVariablesAndEnvironments',
  GenerateProjectCode = 'generateProjectCode',
  VersionsAndDeployments = 'versionsAndDeployments',
  EnjoyYourProject = 'enjoyYourProject',
  ProjectMembers = 'projectMembers',
  Deploy = 'deploy',
  Server = 'server',
  ShareApplication = 'shareApplication',
}

/* Steps */
// 1. Welcome
// 2. Project Description
// 3. Project Information
// 4. Project Members
// 5. Scope of Work
// 6. Data Models and Database
// 7. API Endpoints
// 8. Design System
// 9. Theme
// 10. UI Page Layouts
// 11. UI Pages
// 12. UI Business Processes
// 13. Message Templates
// 14. Server Side Business Processes
// 15. Settings, Variables and Environments
// 16. Generate Project Code
// 17. Versions & Deployments
// 18. Enjoy Your Project


export const WIZARD_STEPS_CONFIG: WizardStepConfig[] = [
  /* 1 Welcome */
  {
    key: WizardSteps.Welcome,
    title: 'Welcome', // Title of the step
    planTitle: 'Generate a plan to create a full-stack web application code from your description', // Title of the timeline plan card
    description: welcomeStepContent, // Description of the step, displayed in the step card
    introduction: welcomeStepIntroductionContent, // Introduction content displayed with rabbit
    introductionExtra: welcomeStepIntroductionExtraContent, // Extra content displayed with rabbit, under the introduction content
    collapsePanel: CollapsePanels.ProjectTitle,
    icon: {
      name: 'fas fa-sparkles'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.Introduction,
    layoutPaths: {
      codePath: 'backend/src/application.ts',
      designPath: '/projects/edit/{{projectId}}'
    },
    layoutMode: LayoutMode.ProjectDesign,
    introductionNextButtonTitle: IntroductionNextButton.LetsGo,
    displayInPlanningTimeline: false,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* 2 ProjectDescription */
  {
    key: WizardSteps.ProjectDescription,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.ImproveProjectDescription,
    title: 'Let\'s Improve Your Project Description',
    planTitle: 'Improve your project description with AI', // Title of the timeline plan card
    description: 'Please wait while we are improving your project description with AI',
    introduction: projectDescriptionStepIntroductionContent,
    introductionExtra: projectDescriptionStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.ProjectDescription,
    icon: {
      name: 'fas fa-lightbulb'
    },
    highlightedElements: [ProjectDesignSettingButtons.ImproveProjectWithAI],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    disableNextButton: true,
    skippable: true,
    layoutPaths: {
      codePath: 'backend/src/application.ts',
      designPath: '/projects/edit/{{projectId}}'
    },
    layoutMode: LayoutMode.ProjectDesign,
    introductionNextButtonTitle: IntroductionNextButton.ProjectDescription,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 15000,
    autoSkipDurationOnDesignView: 5000
  },
  /* 3 ProjectInformation */
  {
    key: WizardSteps.ProjectInformation,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.UpdateProjectInformation,
    title: 'Your Project Information',
    planTitle: 'Generate your stack and scaffolding', // Title of the timeline plan
    description: 'Please wait while we are generating your project scaffolding',
    introduction: projectInformationStepIntroductionContent,
    introductionExtra: projectInformationStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.ProjectInformation,
    icon: {
      name: 'fas fa-users'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutPaths: {
      designPath: '/project-information',
      codePath: 'backend/src/application.ts'
    },
    layoutMode: LayoutMode.ProjectDesign,
    introductionNextButtonTitle: IntroductionNextButton.ProjectInformation,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 20000,
    autoSkipDurationOnDesignView: 5000
  },
  /* ScopeOfWork */
  {
    key: WizardSteps.ScopeOfWork,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateScopeOfWork,
    title: 'Generate Scope of Work',
    planTitle: 'Generate a scope of work for your project', // Title of the timeline plan
    description: 'Please wait while we are generating your scope of work. This may take a few minutes',
    introduction: scopeOfWorkStepIntroductionContent,
    introductionExtra: scopeOfWorkStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.ScopeOfWork,
    icon: {
      name: 'fas fa-file-alt'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutPaths: {
      designPath: '/scope-of-work'
    },
    layoutMode: LayoutMode.ProjectDesign,
    introductionNextButtonTitle: IntroductionNextButton.ScopeOfWork,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* DataModelsAndDatabase */
  {
    key: WizardSteps.DataModelsAndDatabase,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateDataModelsAndDatabase,
    title: 'Generate Data Models and Database',
    planTitle: 'Generate data models and data schemas and initialize the database', // Title of the timeline plan
    description: 'Please wait while we are generating your data models and database. You can customize and add more data models later',
    introduction: dataModelsAndDatabaseStepsIntroductionContent,
    introductionExtra: dataModelsAndDatabaseStepsIntroductionExtraContent,
    collapsePanel: CollapsePanels.DataModelsAndDatabase,
    icon: {
      name: 'fas fa-database'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutPaths: {
      designPath: '/db-diagram-editor'
    },
    layoutMode: LayoutMode.ProjectDesign,
    introductionNextButtonTitle: IntroductionNextButton.DataModelsAndDatabase,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* APIEndpoints */
  {
    key: WizardSteps.APIEndpoints,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateAPIEndpoints,
    title: 'Generate API Endpoints',
    planTitle: 'Generate API endpoints and services', // Title of the timeline plan
    description: 'Please wait while we are generating your API endpoints and services. You can customize the API endpoints later',
    introduction: apiEndpointsStepIntroductionContent,
    introductionExtra: apiEndpointsStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.APIEndpoints,
    icon: {
      name: 'fas fa-cogs'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutMode: LayoutMode.ProjectDesign,
    layoutPaths: {
      designPath: '/projects/endpoints'
    },
    introductionNextButtonTitle: IntroductionNextButton.APIEndpoints,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* DesignSystem */
  {
    key: WizardSteps.DesignSystem,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateDesignSystem,
    title: 'Generate Your UI Design System',
    planTitle: 'Generate UI design system, package the components', // Title of the timeline plan
    description: 'Please wait while we are generating your design system, components and their styles. You can use these components in your application later',
    introduction: designSystemStepIntroductionContent,
    introductionExtra: designSystemStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.DesignSystem,
    icon: {
      name: 'fas fa-swatchbook'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutMode: LayoutMode.ProjectDesign,
    introductionNextButtonTitle: IntroductionNextButton.DesignSystem,
    layoutPaths: {
      designPath: '/component-showroom'
    },
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* Theme */
  {
    key: WizardSteps.Theme,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateTheme,
    title: 'Generate UI Application Theme',
    planTitle: 'Generate UI application theme and styles, colors and sizes', // Title of the timeline plan
    description: 'Please wait while we are generating your theme, styles and colors. You can customize the theme later',
    introduction: themeStepIntroductionContent,
    introductionExtra: themeStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.Theme,
    icon: {
      name: 'fas fa-paint-brush'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutMode: LayoutMode.ProjectDesign,
    introductionNextButtonTitle: IntroductionNextButton.Theme,
    layoutPaths: {
      designPath: '/projects/theme-settings/{{activeProjectThemeId}}'
    },
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* UIPageLayouts */
  {
    key: WizardSteps.UIPageLayouts,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateUIPageLayouts,
    title: 'Generate UI Application Layouts',
    planTitle: 'Generate UI application layouts and wireframes', // Title of the timeline
    description: 'Please wait while we are generating your UI application layouts and wireframes. You can customize the layouts later',
    introduction: uiPageLayoutsStepIntroductionContent,
    introductionExtra: uiPageLayoutsStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.UIPageLayouts,
    icon: {
      name: 'fas fa-columns'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutMode: LayoutMode.ProjectDesign,
    introductionNextButtonTitle: IntroductionNextButton.UIPageLayouts,
    layoutPaths: {
      designPath: '/content-editor/{{activePageLayoutId}}'
    },
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* UIPages */
  {
    key: WizardSteps.UIPages,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateUIPages,
    title: 'Generate UI Application Pages',
    planTitle: 'Generate UI application pages and contents', // Title of the timeline
    description: 'Please wait while we are generating your UI application pages and contents. You can customize and add more pages later',
    introduction: uiPagesStepIntroductionContent,
    introductionExtra: uiPagesStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.UIPages,
    icon: {
      name: 'fas fa-file-code'
    },
    highlightedElements: [ProjectDesignSettingButtons.NextUIPageButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutMode: LayoutMode.ProjectDesign,
    layoutPaths: {
      designPath: '/content-editor/{{activePageId}}',
    },
    introductionNextButtonTitle: 'Generate Client Application Pages',
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* UIBusinessProcesses */
  {
    key: WizardSteps.UIBusinessProcesses,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateUIBusinessProcesses,
    title: 'Generate Event-Based UI Architecture',
    planTitle: 'Generate event-based UI architecture and workflows and wire the data from database to ui elements using the apis', // Title of the timeline
    description: 'Please wait while we are generating your event-based UI architecture and workflows. You can customize the workflows and UI data stores later',
    introduction: uiBusinessProcessesStepIntroductionContent,
    introductionExtra: uiBusinessProcessesStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.UIBusinessProcesses,
    icon: {
      name: 'fas fa-network-wired'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutMode: LayoutMode.ProjectDesign,
    layoutPaths: {
      designPath: '/ui-workflow-diagram'
    },
    introductionNextButtonTitle: IntroductionNextButton.UIBusinessProcesses,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* 13 MessageTemplates */
  {
    key: WizardSteps.MessageTemplates,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateMessageTemplates,
    title: 'Generate Message & Document Templates',
    planTitle: 'Generate message and document templates', // Title of the timeline
    description: 'Please wait while we are generating your message and document templates. You can customize and add more templates later',
    introduction: messageTemplatesStepIntroductionContent,
    introductionExtra: messageTemplatesStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.MessageTemplates,
    icon: {
      name: 'fas fa-envelope'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutMode: LayoutMode.ProjectDesign,
    layoutPaths: {
      designPath: 'projects/edit-message-template/{{activeMessageTemplateId}}'
    },
    introductionNextButtonTitle: IntroductionNextButton.MessageTemplates,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* ServerSideBusinessProcesses */
  {
    key: WizardSteps.ServerSideBusinessProcesses,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateServerSideBusinessProcesses,
    title: 'Generate Micro Services and Workflows',
    planTitle: 'Generate micro services and workflows', // Title of the timeline
    description: 'Please wait while we are setup and deploy an environment for you to create and run your micro services and workflows. You can build AI or custom agent workflows',
    introduction: serverSideBusinessProcessesStepIntroductionContent,
    introductionExtra: serverSideBusinessProcessesStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.ServerSideBusinessProcesses,
    icon: {
      name: 'fas fa-calendar-plus'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutMode: LayoutMode.ProjectDesign,
    layoutPaths: {
      designPath: '/workflow-diagram'
    },
    introductionNextButtonTitle: IntroductionNextButton.ServerSideBusinessProcesses,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* SettingsVariablesAndEnvironments */
  {
    key: WizardSteps.SettingsVariablesAndEnvironments,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateSettingsVariablesAndEnvironments,
    title: 'Generate Environments and Variables',
    planTitle: 'Generate settings, environments and environment variables', // Title of the timeline
    description: 'Please wait while we are generating your settings, environments and environment variables. You can customize and add more variables later',
    introduction: environmentVariablesStepIntroductionContent,
    introductionExtra: environmentVariablesStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.SettingsVariablesAndEnvironments,
    icon: {
      name: 'fas fa-code'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: true,
    layoutMode: LayoutMode.ProjectDesign,
    layoutPaths: {
      codePath: 'loopback4/environments/dev.env',
      designPath: '/projects/project-settings'
    },
    introductionNextButtonTitle: IntroductionNextButton.SettingsVariablesAndEnvironments,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* GenerateProjectCode */
  {
    key: WizardSteps.GenerateProjectCode,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.GenerateProjectCode,
    title: 'Your Applications and Code is Ready!',
    planTitle: 'Verify and generate the remaining code...', // Title of the timeline
    description: 'Please wait while we are generating your updated code. This may take a few minutes. You can customize your code on code view',
    introduction: generateProjectCodeStepIntroductionContent,
    introductionExtra: generateProjectCodeStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.GenerateProjectCode,
    icon: {
      name: 'fas fa-code'
    },
    highlightedElements: [ProjectDesignSettingButtons.WizardNextButton],
    isCompleted: false,
    status: WizardStepStatus.NotStarted,
    skippable: false,
    layoutMode: LayoutMode.ProjectDesign,
    layoutPaths: {
      designPath: '/projects/version-detail/{{latestProjectVersionId}}'
    },
    introductionNextButtonTitle: IntroductionNextButton.GenerateProjectCode,
    displayInPlanningTimeline: true,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* AFTER ShareApplication */
  {
    key: WizardSteps.ShareApplication,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.ShareApplication,
    title: 'Share Application',
    planTitle: 'Share your application on social media to unlock deployment options',
    description: '',
    introduction: '',
    introductionExtra: '',
    collapsePanel: CollapsePanels.ShareApplication,
    icon: {
      name: 'fas fa-users'
    },
    highlightedElements: [],
    isCompleted: true,
    status: WizardStepStatus.Optional,
    disableNextButton: false,
    skippable: true,
    layoutPaths: {
      designPath: '/complete-wizard'
    },
    layoutMode: LayoutMode.ProjectDesign,
    displayInPlanningTimeline: false,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* AFTER ProjectMembers */
  {
    key: WizardSteps.ProjectMembers,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.ProjectMembers,
    title: 'Invite Others to Your Project',
    planTitle: 'Invite team members and collaborators to your project',
    description: projectMembersStepContent,
    introduction: projectMembersStepIntroductionContent,
    introductionExtra: projectMembersStepIntroductionExtraContent,
    collapsePanel: CollapsePanels.ProjectMembers,
    icon: {
      name: 'fas fa-users'
    },
    highlightedElements: [ProjectDesignSettingButtons.InviteOthers],
    isCompleted: true,
    status: WizardStepStatus.Optional,
    disableNextButton: false,
    skippable: false,
    layoutPaths: {
      designPath: '/projects/members-list'
    },
    layoutMode: LayoutMode.ProjectDesign,
    introductionNextButtonTitle: IntroductionNextButton.ProjectMembers,
    displayInPlanningTimeline: false,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* AFTER Deploy */
  {
    key: WizardSteps.Deploy,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.Deploy,
    title: 'Deploy Your Project',
    planTitle: 'Deploy your application to production environment',
    description: '',
    introduction: '',
    introductionExtra: '',
    collapsePanel: CollapsePanels.Deploy,
    icon: {
      name: 'fas fa-users'
    },
    highlightedElements: [],
    isCompleted: true,
    status: WizardStepStatus.ScaffoldedFromTemplate,
    disableNextButton: false,
    skippable: true,
    layoutPaths: {
      designPath: '/projects/live-preview'
    },
    layoutMode: 'projectDesign',
    displayInPlanningTimeline: false,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  },
  /* AFTER Server */
  {
    key: WizardSteps.Server,
    projectDesignTaskIdentifier: ProjectDesignTaskIdentifier.Server,
    title: 'Server',
    planTitle: 'Configure server settings and infrastructure',
    description: '',
    introduction: '',
    introductionExtra: '',
    collapsePanel: CollapsePanels.Server,
    icon: {
      name: 'fas fa-users'
    },
    highlightedElements: [],
    isCompleted: true,
    status: WizardStepStatus.ScaffoldedFromTemplate,
    disableNextButton: false,
    skippable: true,
    layoutPaths: {
      designPath: '/server-page'
    },
    layoutMode: 'projectDesign',
    displayInPlanningTimeline: false,
    autoSkipDurationOnPlanning: 10000,
    autoSkipDurationOnDesignView: 5000
  }
];

